<template>
  <div class="p-4">
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <!-- <Loader /> -->
    </div>

    <div v-else class="bg-white rounded shadow p-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 class="h4 fw-semibold text-dark mb-0">รายงานเวลาเข้า-ออกงาน</h2>
      </div>

      <!-- Filter เลือกเดือนและปี -->
      <div class="row g-3 mb-3">
        <div class="col-md-3">
          <label for="monthSelect" class="form-label">เดือน</label>
          <select
            id="monthSelect"
            v-model="month"
            @change="onFilterChange"
            class="form-select form-select-sm"
          >
            <option value="1">มกราคม</option>
            <option value="2">กุมภาพันธ์</option>
            <option value="3">มีนาคม</option>
            <option value="4">เมษายน</option>
            <option value="5">พฤษภาคม</option>
            <option value="6">มิถุนายน</option>
            <option value="7">กรกฎาคม</option>
            <option value="8">สิงหาคม</option>
            <option value="9">กันยายน</option>
            <option value="10">ตุลาคม</option>
            <option value="11">พฤศจิกายน</option>
            <option value="12">ธันวาคม</option>
          </select>
        </div>

        <div class="col-md-3">
          <label for="yearSelect" class="form-label">ปี</label>
          <select
            id="yearSelect"
            v-model="year"
            @change="onFilterChange"
            class="form-select form-select-sm"
          >
            <option
              v-for="yearOption in availableYears"
              :key="yearOption"
              :value="yearOption"
            >
              {{ yearOption + 543 }} ({{ yearOption }})
            </option>
          </select>
        </div>

        <div class="col-md-3">
          <label for="searchEmployee" class="form-label">ค้นหาพนักงาน</label>
          <div class="input-group input-group-sm">
            <span class="input-group-text">
              <i class="fas fa-search"></i>
            </span>
            <input
              id="searchEmployee"
              v-model="searchTerm"
              type="text"
              class="form-control"
              placeholder="พิมพ์ชื่อพนักงาน..."
              @input="onSearchChange"
            />
            <button
              v-if="searchTerm"
              @click="clearSearch"
              class="btn btn-outline-secondary"
              type="button"
              title="ล้างการค้นหา"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="col-md-3 d-flex align-items-end gap-2">
          <button
            @click="refreshData"
            class="btn btn-outline-primary btn-sm"
            :disabled="loading"
            title="รีเฟรชข้อมูล"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            <i v-else class="fas fa-sync-alt me-1"></i>
            รีเฟรช
          </button>

          <button
            @click="exportToExcel"
            class="btn btn-success btn-sm"
            :disabled="filteredEmployees.length === 0"
            :title="
              filteredEmployees.length === 0
                ? 'ไม่มีข้อมูลสำหรับ export'
                : 'ดาวน์โหลดไฟล์ Excel'
            "
          >
            <i class="fas fa-file-excel me-1"></i>
            Excel
          </button>
        </div>
      </div>

      <!-- แสดงข้อมูลที่เลือก -->
      <div class="alert alert-info alert-sm mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <i class="fas fa-info-circle me-1"></i>
            กำลังแสดงข้อมูลเดือน {{ getMonthName(month) }} ปี
            {{ year + 543 }} ({{ year }})
          </div>
          <div class="small">
            <i class="fas fa-users me-1"></i>
            <span v-if="searchTerm">
              แสดง {{ employeeCount }} จาก {{ totalEmployeeCount }} คน
            </span>
            <span v-else> พนักงานทั้งหมด {{ totalEmployeeCount }} คน </span>
          </div>
        </div>
        <div v-if="searchTerm" class="mt-1 small">
          <i class="fas fa-search me-1"></i>
          <span class="text-muted">ค้นหา:</span>
          <span class="fw-bold text-primary">{{ searchTerm }}</span>
          <button
            @click="clearSearch"
            class="btn btn-link btn-sm p-0 ms-1 text-decoration-none"
            title="ล้างการค้นหา"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-sm text-center table-custom">
          <thead class="table-light">
            <tr class="fw-bold border-bottom">
              <th
                class="sticky-col-1 bg-light px-3 py-3 text-center text-nowrap"
              >
                พนักงาน
              </th>
              <th
                class="sticky-col-2 bg-light px-3 py-3 text-center text-nowrap"
              >
                รายการ
              </th>
              <th
                v-for="date in dateColumns"
                :key="date"
                class="px-3 py-3 text-center text-nowrap date-col"
              >
                {{ new Date(date).getDate() }}
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- แสดงข้อความเมื่อไม่พบข้อมูล -->
            <tr v-if="filteredEmployees.length === 0">
              <td
                :colspan="dateColumns.length + 2"
                class="text-center py-4 text-muted"
              >
                <div v-if="loading">
                  <div
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></div>
                  กำลังโหลดข้อมูล...
                </div>
                <div v-else-if="searchTerm">
                  <i class="fas fa-search me-2"></i>
                  ไม่พบพนักงานที่ชื่อ "{{ searchTerm }}"
                </div>
                <div v-else>
                  <i class="fas fa-user-slash me-2"></i>
                  ไม่มีข้อมูลพนักงาน
                </div>
              </td>
            </tr>

            <template
              v-for="(employee, empIndex) in filteredEmployees"
              :key="empIndex"
            >
              <!-- แถวสาขาที่ทำงาน -->
              <tr class="table-hover-row">
                <td
                  :rowspan="8"
                  class="sticky-col-1 bg-white text-center fw-semibold text-warning align-middle"
                >
                  <div class="h5 fw-bold text-nowrap px-2">
                    {{ employee.name }}
                  </div>
                </td>
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  สาขาที่ทำงาน
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-branch-${date}`"
                  class="px-2 py-2 small text-center text-nowrap"
                >
                  {{ getBranchData(employee[date]) }}
                </td>
              </tr>

              <!-- แถวเวลาเข้า-ออก -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  เวลาเข้า-ออก
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-time-${date}`"
                  class="px-2 py-2 small text-center text-nowrap"
                >
                  {{ getTimeData(employee[date]) }}
                </td>
              </tr>

              <!-- แถวยอดขาย -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  ยอดขาย
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-sales-${date}`"
                  class="px-2 py-2 small text-center text-success fw-semibold text-nowrap"
                >
                  {{ getSalesData(employee[date]) }}
                </td>
              </tr>

              <!-- แถวกำไร -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  กำไร
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-profit-${date}`"
                  class="px-2 py-2 small text-center text-nowrap"
                >
                  {{ getProfitData(employee[date]) }}
                </td>
              </tr>

              <!-- แถวเวลาทำงาน -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  เวลาทำงาน
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-work-${date}`"
                  class="px-2 py-2 small text-center text-primary text-nowrap"
                >
                  {{ getWorkingHours(employee[date]) }}
                </td>
              </tr>

              <!-- แถวโอที -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  โอที
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-ot-${date}`"
                  class="px-2 py-2 small text-center text-warning fw-semibold text-nowrap"
                >
                  {{ getOvertimeHours(employee[date]) }}
                </td>
              </tr>

              <!-- แถวสาย -->
              <tr class="table-hover-row">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  สาย
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-late-${date}`"
                  class="px-2 py-2 small text-center text-danger text-nowrap"
                >
                  {{ getLateData(employee[date]) }}
                </td>
              </tr>

              <!-- แถวสถานะ -->
              <tr class="table-hover-row border-bottom border-2">
                <td
                  class="sticky-col-2 bg-light px-3 py-2 text-start fw-medium text-nowrap"
                >
                  สถานะ/หมายเหตุ
                </td>
                <td
                  v-for="date in dateColumns"
                  :key="`${empIndex}-status-${date}`"
                  class="px-2 py-2 small text-center fw-semibold text-nowrap"
                  :class="getStatusClass(employee[date])"
                >
                  {{ getStatus(employee[date]) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import userApi from "@/api/user/";
// import Loader from "@/components/loaderIndex.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
  components: {
    // Loader,
  },
  data: () => ({
    loading: false,
    month: new Date().getMonth() + 1, // เดือนปัจจุบัน
    year: new Date().getFullYear(), // ปีปัจจุบัน
    dateColumns: [],
    dataItemsShow: [],
    availableYears: [], // รายการปีที่ใช้งานได้
    searchTerm: "", // คำค้นหาพนักงาน
  }),
  created() {
    this.initializeYears();
    this.generateDateColumns();
    this.getAttendanceReport();
  },
  computed: {
    // Filter ข้อมูลพนักงานตาม search term
    filteredEmployees() {
      if (!this.searchTerm.trim()) {
        return this.dataItemsShow;
      }

      const searchLower = this.searchTerm.toLowerCase().trim();
      return this.dataItemsShow.filter(
        (employee) =>
          employee.name && employee.name.toLowerCase().includes(searchLower)
      );
    },

    // นับจำนวนพนักงานที่แสดง
    employeeCount() {
      return this.filteredEmployees.length;
    },

    // นับจำนวนพนักงานทั้งหมด
    totalEmployeeCount() {
      return this.dataItemsShow.length;
    },
  },
  watch: {
    // เฝ้าการเปลี่ยนแปลงของเดือนและปี
    month(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.generateDateColumns();
        this.getAttendanceReport();
      }
    },
    year(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.generateDateColumns();
        this.getAttendanceReport();
      }
    },
  },
  methods: {
    // กำหนดรายการปีที่ใช้งานได้ (5 ปีย้อนหลัง ถึง 2 ปีข้างหน้า)
    initializeYears() {
      const currentYear = new Date().getFullYear();
      this.availableYears = [];
      for (let i = currentYear - 5; i <= currentYear + 2; i++) {
        this.availableYears.push(i);
      }
    },

    // แปลงเลขเดือนเป็นชื่อเดือน
    getMonthName(month) {
      const monthNames = [
        "",
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
      ];
      return monthNames[month] || "";
    },

    // จัดการการเปลี่ยนแปลง filter
    onFilterChange() {
      // แปลงเป็น integer เพื่อให้แน่ใจ
      this.month = parseInt(this.month);
      this.year = parseInt(this.year);

      this.generateDateColumns();
      this.getAttendanceReport();
    },

    // รีเฟรชข้อมูล
    refreshData() {
      this.generateDateColumns();
      this.getAttendanceReport();
    },

    // จัดการการค้นหาพนักงาน
    onSearchChange() {
      // ไม่ต้องทำอะไรพิเศษ เพราะ computed property จะทำงานอัตโนมัติ
    },

    // ล้างการค้นหา
    clearSearch() {
      this.searchTerm = "";
    },

    generateDateColumns() {
      const daysInMonth = new Date(this.year, this.month, 0).getDate();
      this.dateColumns = Array.from({ length: daysInMonth }, (_, i) => {
        return `${this.year}-${String(this.month).padStart(2, "0")}-${String(
          i + 1
        ).padStart(2, "0")}`;
      });
    },
    async getAttendanceReport() {
      this.loading = true;
      try {
        let res = await userApi.user.getAttendanceReport(this.month, this.year);
        const result = res;
        if (result.response_status === "SUCCESS") {
          this.dataItemsShow = result.data.rows;
          this.dateColumns = result.data.dates;
        }
      } catch (err) {
        console.error("Error loading attendance report:", err);
      } finally {
        this.loading = false;
      }
    },
    // ฟังก์ชันดึงข้อมูลเวลาเข้า-ออก (บรรทัดที่ 2)
    getTimeData(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[1] || "-"; // บรรทัดที่ 2 = index 1
    },

    // ฟังก์ชันดึงข้อมูลยอดขาย (บรรทัดที่ 3)
    getSalesData(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[2] || "-"; // บรรทัดที่ 3 = index 2
    },

    // ฟังก์ชันดึงข้อมูลสาขา (บรรทัดที่ 1)
    getBranchData(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[0] || "-"; // บรรทัดที่ 1 = index 0
    },

    // ฟังก์ชันดึงข้อมูลกำไร (บรรทัดที่ 4)
    getProfitData(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[3] || "-"; // บรรทัดที่ 4 = index 3
    },

    // ฟังก์ชันดึงข้อมูลสาย (บรรทัดที่ 7)
    getLateData(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[6] || "-"; // บรรทัดที่ 7 = index 6
    },

    // ฟังก์ชันดึงเวลาทำงาน (บรรทัดที่ 5)
    getWorkingHours(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[4] || "-"; // บรรทัดที่ 5 = index 4
    },

    // ฟังก์ชันดึงเวลาโอที (บรรทัดที่ 6)
    getOvertimeHours(dayData) {
      if (!dayData || dayData === "-") return "-";
      const lines = dayData.split("\n");
      return lines[5] || "-"; // บรรทัดที่ 6 = index 5
    },

    // ฟังก์ชันดึงสถานะ (บรรทัดที่ 8)
    getStatus(dayData) {
      if (!dayData || dayData === "-") return "ขาดงาน";
      const lines = dayData.split("\n");
      console.log(lines);
      return lines[7] || "ขาดงาน"; // บรรทัดที่ 8 = index 7
    },

    // ฟังก์ชันกำหนด CSS class สำหรับสถานะ
    getStatusClass(dayData) {
      const status = this.getStatus(dayData);
      switch (status) {
        case "เข้างาน":
          return "text-success";
        case "ระหว่างทำงาน":
          return "text-primary";
        case "ขาดงาน":
          return "text-danger";
        case "ลาไม่รับค่าจ้าง":
          return "text-warning";
        case "ลากิจ":
          return "text-primary";
        case "ลาป่วย":
          return "text-info";
        case "ลาพักร้อน":
          return "text-secondary";
        default:
          return "text-muted";
      }
    },

    exportToExcel() {
      const wb = XLSX.utils.book_new();

      // เตรียม header ตามรูปแบบตารางใหม่
      const headers = [
        "พนักงาน",
        "รายการ",
        ...this.dateColumns.map((date) => new Date(date).getDate()),
      ];

      const rows = [];
      const categories = [
        "สาขาที่ทำงาน",
        "เวลาเข้า-ออก",
        "ยอดขาย",
        "กำไร",
        "เวลาทำงาน",
        "โอที",
        "สาย",
        "สถานะ/หมายเหตุ",
      ];

      // สร้างข้อมูลแถวใหม่ (แต่ละพนักงานมี 8 แถว) - ใช้ข้อมูลที่ filter แล้ว
      this.filteredEmployees.forEach((employee) => {
        categories.forEach((category, categoryIndex) => {
          const row = [];

          // คอลัมน์พนักงาน (ใช้ชื่อในแถวแรกเท่านั้น)
          if (categoryIndex === 0) {
            row.push(employee.name || "-");
          } else {
            row.push(""); // แถวอื่นๆ ให้ว่าง
          }

          // คอลัมน์รายการ
          row.push(category);

          // คอลัมน์วันที่
          this.dateColumns.forEach((date) => {
            const dayData = employee[date] || "-";
            let cellValue = "-";

            switch (category) {
              case "สาขาที่ทำงาน":
                cellValue = this.getBranchData(dayData);
                break;

              case "เวลาเข้า-ออก":
                cellValue = this.getTimeData(dayData);
                break;

              case "ยอดขาย":
                const salesData = this.getSalesData(dayData);
                if (salesData !== "-" && salesData.includes("฿")) {
                  // เอาเฉพาะตัวเลขสำหรับ Excel (ไม่รวม ฿ และ comma)
                  cellValue = salesData
                    .replace("฿", "")
                    .replace(/,/g, "")
                    .trim();
                } else {
                  cellValue = salesData === "-" ? "-" : salesData;
                }
                break;

              case "กำไร":
                const profitData = this.getProfitData(dayData);
                if (profitData !== "-" && profitData.includes("฿")) {
                  cellValue = profitData
                    .replace("฿", "")
                    .replace(/,/g, "")
                    .trim();
                } else {
                  cellValue = profitData === "-" ? "-" : profitData;
                }
                break;

              case "เวลาทำงาน":
                const workHours = this.getWorkingHours(dayData);
                // แปลงจาก "8.0 ชั่วโมง" เป็น "8.0" สำหรับ Excel
                if (workHours !== "-" && workHours.includes("ชั่วโมง")) {
                  cellValue = workHours.replace("ชั่วโมง", "").trim();
                } else {
                  cellValue = workHours === "-" ? "-" : workHours;
                }
                break;

              case "โอที":
                const otHours = this.getOvertimeHours(dayData);
                // แปลงจาก "6.0 ชั่วโมง" เป็น "6.0" สำหรับ Excel
                if (otHours !== "-" && otHours.includes("ชั่วโมง")) {
                  cellValue = otHours.replace("ชั่วโมง", "").trim();
                } else {
                  cellValue = otHours === "-" ? "-" : otHours;
                }
                break;

              case "สาย":
                const lateData = this.getLateData(dayData);
                cellValue = lateData === "-" ? "-" : lateData;
                break;

              case "สถานะ/หมายเหตุ":
                cellValue = this.getStatus(dayData);
                break;
            }

            row.push(cellValue);
          });

          rows.push(row);
        });
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // ปรับ merge cells สำหรับชื่อพนักงาน
      const merges = [];
      let currentRow = 2; // เริ่มจากแถวที่ 2 (หลัง header)

      this.filteredEmployees.forEach(() => {
        // Merge 8 แถวสำหรับชื่อพนักงาน (8 categories)
        merges.push({
          s: { r: currentRow - 1, c: 0 }, // start row, column
          e: { r: currentRow + 6, c: 0 }, // end row, column (รวม 8 แถว)
        });
        currentRow += 8;
      });

      ws["!merges"] = merges;

      XLSX.utils.book_append_sheet(wb, ws, "Attendance");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      // สร้างชื่อไฟล์ที่อ่านง่าย
      const monthName = this.getMonthName(this.month);
      let fileName = `รายงานเวลาเข้า-ออกงาน_${monthName}_${this.year + 543}`;

      // เพิ่มข้อมูลการค้นหาในชื่อไฟล์ถ้ามี
      if (this.searchTerm) {
        fileName += `_${this.searchTerm}`;
      }

      fileName += ".xlsx";

      saveAs(new Blob([wbout], { type: "application/octet-stream" }), fileName);
    },
  },
};
</script>

<style scoped>
/* Custom table styling */
.table-custom {
  min-width: 3000px;
  font-size: 13px;
}

.table-custom th {
  font-weight: 600;
  font-size: 14px;
}

/* Sticky columns */
.sticky-col-1 {
  position: sticky;
  left: 0;
  z-index: 20;
  width: 150px;
  border-right: none !important;
}

.sticky-col-2 {
  position: sticky;
  left: 150px;
  z-index: 21;
  width: 180px;
  border-left: none !important;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.date-col {
  width: 80px;
  min-width: 80px;
}

/* Table responsive styling */
.table-responsive {
  max-height: 80vh;
}

/* Row hover effect */
.table-hover-row:hover {
  background-color: #f8f9fa !important;
}

/* Prevent text wrapping */
.text-nowrap {
  white-space: nowrap;
}

/* Border utilities */
.border-bottom {
  border-bottom: 1px solid #dee2e6;
}

.border-2 {
  border-width: 2px !important;
}

/* Alert styling */
.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

/* Form styling */
.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

/* Search input styling */
.input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.input-group .form-control {
  border-color: #ced4da;
}

.input-group .form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Search term highlight */
.fw-bold.text-primary {
  background-color: rgba(13, 110, 253, 0.1);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
}
</style>
